import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserProgress {
  id: string;
  user_id: string;
  program_id: string;
  current_week: number;
  completed_weeks: number[];
  unlocked_weeks: number[];
  created_at: string;
  updated_at: string;
}

/**
 * Single-row progress lookup for a (user, program/cohort) pair.
 * Returns `null` when no row exists yet (e.g. a founder who hasn't completed Week 0).
 */
export function useUserProgress(userId: string | null | undefined, programId: string | null | undefined) {
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!userId || !programId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('program_id', programId)
        .maybeSingle();
      if (error) throw error;
      const normalized = data
        ? {
            ...data,
            unlocked_weeks: data.unlocked_weeks ?? [],
            completed_weeks: data.completed_weeks ?? [],
          }
        : null;
      setProgress(normalized as UserProgress | null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch progress');
    } finally {
      setLoading(false);
    }
  }, [userId, programId]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  return { progress, loading, error, refetch: fetchProgress };
}

/** Fetch progress for many users in a cohort (admin grid). */
export function useCohortProgress(programId: string | null | undefined) {
  const [rows, setRows] = useState<UserProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    if (!programId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('program_id', programId);
      if (error) throw error;
      const normalized = ((data as UserProgress[]) ?? []).map((r) => ({
        ...r,
        unlocked_weeks: r.unlocked_weeks ?? [],
        completed_weeks: r.completed_weeks ?? [],
      }));
      setRows(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cohort progress');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return { rows, loading, error, refetch: fetchRows };
}

/** True when the week is at or below current_week, or has been manually unlocked. Week 0 is always open. */
export function isWeekAccessibleFromProgress(weekNumber: number, progress: UserProgress | null): boolean {
  if (weekNumber === 0) return true;
  if (!progress) return false;
  if (weekNumber <= progress.current_week) return true;
  if ((progress.unlocked_weeks ?? []).includes(weekNumber)) return true;
  return false;
}

export function getWeekStatusFromProgress(
  weekNumber: number,
  progress: UserProgress | null,
): 'locked' | 'available' | 'current' | 'complete' {
  if ((progress?.completed_weeks ?? []).includes(weekNumber)) return 'complete';
  if (progress && weekNumber === progress.current_week) return 'current';
  if (isWeekAccessibleFromProgress(weekNumber, progress)) return 'available';
  return 'locked';
}

/**
 * Resilient access resolver for the founder UI. Treats a week as accessible if ANY trusted
 * source confirms it: user_progress (primary), accelerator_startups.current_week mirror,
 * a manual unlock on the submission row, or a completed submission for that week.
 *
 * This prevents the founder portal from showing a week as "locked" when the backend has
 * already advanced one source of truth but the other hasn't refetched yet.
 */
export interface ResolveAccessInput {
  progress: UserProgress | null;
  startupCurrentWeek: number | null | undefined;
  submissions: Array<{ week_number: number; is_complete: boolean; unlocked_by?: string | null }>;
}

export function resolveEffectiveCurrentWeek(input: ResolveAccessInput): number {
  return Math.max(
    input.progress?.current_week ?? 0,
    input.startupCurrentWeek ?? 0,
  );
}

export function resolveWeekAccess(weekNumber: number, input: ResolveAccessInput): boolean {
  if (weekNumber === 0) return true;
  if (weekNumber <= resolveEffectiveCurrentWeek(input)) return true;
  if ((input.progress?.unlocked_weeks ?? []).includes(weekNumber)) return true;
  const sub = input.submissions.find((s) => s.week_number === weekNumber);
  if (sub?.unlocked_by) return true;
  if (sub?.is_complete) return true;
  // Resilient fallback: the database's advance_startup_week function only opens
  // week N+1 once week N is marked complete, so a completed previous-week
  // submission is sufficient proof of access — even when user_progress and the
  // accelerator_startups mirror are still catching up on a freshly mounted page.
  if (weekNumber > 0) {
    const prevSub = input.submissions.find((s) => s.week_number === weekNumber - 1);
    if (prevSub?.is_complete) return true;
  }
  return false;
}

export function resolveWeekStatus(
  weekNumber: number,
  input: ResolveAccessInput,
): 'locked' | 'available' | 'current' | 'complete' {
  const completedViaProgress = (input.progress?.completed_weeks ?? []).includes(weekNumber);
  const completedViaSubmission = input.submissions.find((s) => s.week_number === weekNumber)?.is_complete;
  if (completedViaProgress || completedViaSubmission) return 'complete';
  const current = resolveEffectiveCurrentWeek(input);
  if (weekNumber === current) return 'current';
  if (resolveWeekAccess(weekNumber, input)) return 'available';
  return 'locked';
}

/** Staff-only: append a week to a founder's unlocked_weeks via SECURITY DEFINER RPC. */
export async function unlockWeekForUser(userId: string, programId: string, weekNumber: number) {
  const { error } = await supabase.rpc('unlock_week_for_user', {
    _user_id: userId,
    _program_id: programId,
    _week_number: weekNumber,
  });
  if (error) throw error;
}
